import { axiosInstance } from "@/configs/axiosConfig";
import keycode from "keycode";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { dnbComLog, isNotUndefined, LogSeverity } from "@/shared/FunctionCore";
import "@/app/pages/dunsOptOut/DunsOptoutToolStyles.scss";
import { optOutUrl } from "@/app/pages/dunsOptOut/constant";

interface ListElement {
  companyCompanyName: string;
  companyCompanyProfileUrl: string;
  companyDuns: string;
  dunsOptoutExpiration: string | undefined | null;
  dunsOptoutId: number;
  dunsOptoutSubmitted: string;
  requestorName: string;
  requestTypeName: string;
}

interface RowProps {
  "businessDirectoryOptoutToolServletKey": string;
  "listElement": ListElement;
  "setSubmitButtonClicked": Dispatch<SetStateAction<boolean>>;
}

const baseUrl = "https://author1useast1.qa2.dbradstreet.adobecqms.net";
const adobeEndpoint = `${baseUrl}/apps/public/content/BusinessDirectoryOptoutToolPostServlet?`;
let aURL = "/business-directory/companyOptout?key=bae5c27b1a245e1c24064127128ffce40913e87b63e98b726a636e774fca0fb5&servletRequestType=getDunsOptoutEntries&limit=100&offset=0";
const servletKey = "bae5c27b1a245e1c24064127128ffce40913e87b63e98b726a636e774fca0fb5";

const optoutRequestTypeArray = [
  "Contacts Only",
  "Data update (temporary)",
  "Full removal (delist rqd)",
  "Revenue Only",
  "Company Description",
] as const;

type OptoutRequestType = typeof optoutRequestTypeArray[number];

const stringToOptoutRequestType = (st: string): OptoutRequestType | undefined =>
  optoutRequestTypeArray.find((op) => st === op);

interface OptoutRequestTypeSelectProperties {
  "labelText": string | undefined;
  "optoutRequestType": OptoutRequestType;
  "setOptoutRequestType": (optoutRequestType: OptoutRequestType) => void;
}

const OptoutRequestTypeSelect = (props: OptoutRequestTypeSelectProperties) => {
  const { labelText, optoutRequestType, setOptoutRequestType } = props;

  const selectElement = (
    <select
      className="optoutRequests"
      onChange={(ch) => setOptoutRequestType(stringToOptoutRequestType(ch.target.value)!)}
      value={optoutRequestType}
    >
      {optoutRequestTypeArray.map((st, nu) => (
        <option key={nu} value={st}>
          {st}
        </option>
      ))}
    </select>
  );

  return isNotUndefined(labelText) ? (
    <label>
      {labelText}
      {selectElement}
    </label>
  ) : (
    selectElement
  );
};

const Row = (props: RowProps) => {
  const { listElement } = props;
  const dunsoptoutexpirationOrEmptyString = listElement.dunsOptoutExpiration ?? "";
  const [optoutExpirationDate, setOptoutExpirationDate] = useState(dunsoptoutexpirationOrEmptyString);
  const [optoutRequestType, setOptoutRequestType] = useState(stringToOptoutRequestType(listElement.requestTypeName)!);
  const [optoutRequester, setOptoutRequester] = useState(listElement.requestorName);
  const [removeSuppression, setRemoveSuppression] = useState(false);

  const editHasBeenMade =
    optoutExpirationDate !== dunsoptoutexpirationOrEmptyString ||
    optoutRequestType !== listElement.requestTypeName ||
    (listElement.requestorName &&
      optoutRequester.localeCompare(listElement.requestorName, "en", { sensitivity: "base" }) !== 0) ||
    removeSuppression;

  function formatDate(inputDate) {
    const date = new Date(inputDate);

    return date
      .toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  }

  return (
    <tr className={`tableRow ${editHasBeenMade ? "editedRow" : ""}`}>
      <td className="companyName">
        <a href={listElement.companyCompanyProfileUrl}>{listElement.companyCompanyName}</a>
      </td>
      <td className="companyDuns">{listElement.companyDuns}</td>
      <td className="expirationDate">
        <input
          className="optoutExpirationDate"
          onChange={(ch) => setOptoutExpirationDate(ch.target.value)}
          type="date"
          value={optoutExpirationDate}
        />
      </td>
      <td className="requestor">
        <input
          className="optoutRequester"
          onChange={(ch) => setOptoutRequester(ch.target.value)}
          value={optoutRequester}
        />
      </td>
      <td className="submitDate">{formatDate(listElement.dunsOptoutSubmitted)}</td>
      <td className="requestType">
        <OptoutRequestTypeSelect
          labelText={void 0}
          optoutRequestType={optoutRequestType}
          setOptoutRequestType={setOptoutRequestType}
        />
      </td>
      <td className="removeSuppression">
        <input
          checked={removeSuppression}
          onChange={(ch) => setRemoveSuppression(ch.target.checked)}
          type="checkbox"
        />
      </td>
      <td>
        <div className="actionsContainer">
          {editHasBeenMade && (
            <button
              className="submitChangesButton"
              onClick={() => {
                axiosInstance
                  .post(optOutUrl, {
                    edit: true,
                    key: servletKey,
                    optoutExpirationDate,
                    optoutRequestType,
                    optoutRequester,
                    optoutTarget: listElement.companyDuns,
                    removeSuppression,
                  })
                  .then(() => props?.setSubmitButtonClicked(true))
                  .catch((error) => {
                    dnbComLog(LogSeverity.ERROR, error);
                  });
              }}
              type="button"
            >
              Submit edit
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

interface BusinessDirectoryOptoutToolComponentProperties {
  "businessDirectoryOptoutToolServletKey": string;
}

const mockData: any = {
  list: [
    {
      dunsOptoutId: "70",
      companyCompanyName: "PREMIUM POINT, LDA",
      companyDuns: "453160921",
      companyCompanyProfileUrl:
        "https://www-devaka.dnb.com/business-directory/company-profiles.premium_point_lda.49507e0c5908a3bddb3d558ae6b78689.html",
      dunsOptoutExpiration: null,
      requestorName: "ANDREW DONOVAN",
      dunsOptoutSubmitted: "01/25/2022 16:11",
      requestTypeName: "Full removal (delist rqd)",
    },
    {
      dunsOptoutId: "80",
      companyCompanyName: "Hopkinson Associates Limited",
      companyDuns: "482063729",
      companyCompanyProfileUrl:
        "https://www-devaka.dnb.com/business-directory/company-profiles.hopkinson_associates_limited.28500f3e51224df3300e328255465c55.html",
      dunsOptoutExpiration: null,
      requestorName: "ANDREW DONOVAN",
      dunsOptoutSubmitted: "01/01/2022 02:11",
      requestTypeName: "Full removal (delist rqd)",
    },
  ],
  count: 5260,
};

interface BusinessDirectoryOptoutToolServletResponse {
  count: number;
  list: ListElement[] | null;
}

const DunsOptoutToolComponent = () => {
  const [businessDirectoryOptoutToolServletResponse, setBusinessDirectoryOptoutToolServletResponse] =
    useState<BusinessDirectoryOptoutToolServletResponse | undefined>();
  const [companyNameDunsEncodedDuns, setCompanyNameDunsEncodedDuns] = useState("");
  const [companyNameDunsEncodedDunsValue, setCompanyNameDunsEncodedDunsValue] = useState("");
  const [loadingState, setLoadingState] = useState(false);
  const [optoutExpirationDate, setOptoutExpirationDate] = useState("");
  const [optoutRequestType, setOptoutRequestType] = useState<OptoutRequestType>("Full removal (delist rqd)");
  const [optoutRequester, setOptoutRequester] = useState("");
  const [optoutTarget, setOptoutTarget] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [submitButtonClicked, setSubmitButtonClicked] = useState(false);

  const pageSize = 100;
  const fetchDunsOptOutData = async () => {
    setLoadingState(true);
    setSubmitButtonClicked(false);

    const params = new URLSearchParams({
      key: servletKey,
      servletrequesttype: "getDunsOptoutEntries",
      limit: String(pageSize),
      offset: String(pageNumber - 1),
      companynamedunsencodedunsvalue: companyNameDunsEncodedDunsValue,
    }).toString();

    const URLWithParams = `${optOutUrl}?${params}`;

    try {
      const response = await axiosInstance.get(URLWithParams);
      setBusinessDirectoryOptoutToolServletResponse(response.data);
      setTotalPages(Math.ceil(response.data.count / pageSize));
    } catch (error) {
      setBusinessDirectoryOptoutToolServletResponse(mockData);
      dnbComLog(LogSeverity.ERROR, "Error message opt-out:", error?.message);
    } finally {
      setLoadingState(false);
    }
  };

  useEffect(() => {
    fetchDunsOptOutData();
  }, [companyNameDunsEncodedDunsValue, pageNumber, submitButtonClicked]);

  return (
    <div className="wrapper">
      <div className="containerTop">
        <h3>
          Note: it will take up to 10 minutes for changes made on this page to be reflected on dnb.com
        </h3>

        <h3>New suppression entry</h3>

        <div className="subSection">
          <label>
            Suppression target (DUNS, encoded DUNS, or company profile page URL):
            {" "}
            <input
              className="optoutTarget"
              onChange={(ch) => {
                setOptoutTarget(ch.target.value);
              }}
              value={optoutTarget}
            />
          </label>
        </div>

        <div className="subSection">
          <label>
            Suppression expiration date:
            {" "}
            <input
              className="optoutExpirationDate"
              onChange={(ch) => {
                setOptoutExpirationDate(ch.target.value);
              }}
              type="date"
              value={optoutExpirationDate}
            />
          </label>
        </div>

        <div className="subSection">
          <label>
            Suppression requester:
            {" "}
            <input
              className="optoutRequester"
              onChange={(ch) => setOptoutRequester(ch.target.value)}
              value={optoutRequester}
            />
          </label>
        </div>

        <div className="subSection">
          <OptoutRequestTypeSelect
            labelText="Suppression request type:"
            optoutRequestType={optoutRequestType}
            setOptoutRequestType={setOptoutRequestType}
          />
        </div>

        <div className="subSection">
          <button
            className="submitChangesButton"
            onClick={() => {
              axiosInstance
                .post(optOutUrl, {
                  edit: false,
                  key: servletKey,
                  optoutExpirationDate,
                  optoutRequestType,
                  optoutRequester,
                  optoutTarget,
                  removeSuppression: false,
                })
                .then(() => {
                  setPageNumber(1);
                  setSubmitButtonClicked(true);
                })
                .catch((error) => {
                  dnbComLog(LogSeverity.ERROR, error);
                });
            }}
            type="button"
          >
            Submit new suppression entry
          </button>
        </div>
      </div>

      <div className="containerTop">
        <h3>Existing suppression entries</h3>

        <div>
          <label>
            Search by company name, DUNS, or encoded DUNS:
            {" "}
            <input
              className="companyNameDunsEncodedDuns"
              onChange={(ch) => setCompanyNameDunsEncodedDuns(ch.target.value)}
              onKeyPress={(ke) => {
                if (ke.which === keycode.codes.enter) {
                  setCompanyNameDunsEncodedDunsValue(companyNameDunsEncodedDuns);
                }
              }}
              value={companyNameDunsEncodedDuns}
            />
          </label>

          <button
            className="search"
            onClick={() => {
              setCompanyNameDunsEncodedDunsValue(companyNameDunsEncodedDuns);
            }}
            type="button"
          >
            Search
          </button>
        </div>

        <div>
          {loadingState ? (
            <div>Loading...</div>
          ) : (
            <>
              {companyNameDunsEncodedDunsValue.length > 0 ? (
                <div className="containerTop">{`Showing results for search term "${companyNameDunsEncodedDunsValue}"`}</div>
              ) : (
                <></>
              )}

              {isNotUndefined(businessDirectoryOptoutToolServletResponse?.list) ? (
                <>
                  <div className="containerTop">
                    Showing{" "}
                    {businessDirectoryOptoutToolServletResponse?.list?.length}{" "}
                    of {businessDirectoryOptoutToolServletResponse?.count} optout entries (newest first)
                  </div>
                  <div className="containerTop">
                    <label>
                      Page{" "}
                      <select
                        className="pageNumber"
                        onChange={(ch) => setPageNumber(Number(ch.target.value))}
                        value={pageNumber}
                      >
                        {Array.from({ length: totalPages }).map((_, index) => {
                          const indexPlusOne = index + 1;
                          return (
                            <option key={indexPlusOne} value={indexPlusOne}>
                              {indexPlusOne}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  </div>
                  <table className="customTable">
                    <thead className="customTableHeader">
                      <tr>
                        <th>Company name (purged company profile page linked)</th>
                        <th>Company DUNS</th>
                        <th>Suppression expiration date (must be in the future)</th>
                        <th>Requestor</th>
                        <th>Submit date</th>
                        <th>Request type</th>
                        <th>Remove suppression (for immediate removals)</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {businessDirectoryOptoutToolServletResponse?.list?.map((li) => (
                        <Row
                          businessDirectoryOptoutToolServletKey={servletKey}
                          key={li.dunsOptoutId}
                          listElement={li}
                          setSubmitButtonClicked={setSubmitButtonClicked}
                        />
                      ))}
                    </tbody>
                  </table>

                </>
              ) : (
                <></>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DunsOptoutToolComponent;
